import * as React from 'react';
import { OhmsLaw } from 'js-electrical-engineering-equations';
import { useCallback, useState } from 'react';
import { Button, Card, CardActions, CardContent, TextField } from '@material-ui/core';
import * as FormulaWheelElectronics from '../../../static/images/FormulaWheelElectronics.png';
import { useLastCalculated } from '../../hooks/useLastCalculated';

const fieldsDefaults = {
  current: '',
  voltage: '',
  resistance: '',
  watts: '',
};

export function OhmsLawComponent(): JSX.Element {
  const [fields, setFields] = useState(fieldsDefaults);
  const { lastCalculated, wasLastCalculated, setLastCalculated } = useLastCalculated<
    keyof typeof fieldsDefaults
  >();

  const clear = useCallback(() => {
    setLastCalculated([]);
    setFields(fieldsDefaults);
  }, [setLastCalculated, setFields]);

  const onChange = useCallback(
    (event) => {
      const field = event.target.name;
      const value = event.target.value;
      setFields({ ...fields, [field]: value });
    },
    [fields, setFields],
  );

  const calculate = useCallback(
    (e) => {
      e.preventDefault();
      const { current, voltage, resistance, watts } = fields;
      if (
        (wasLastCalculated('watts') && wasLastCalculated('resistance')) ||
        (current && voltage && resistance === '' && watts === '')
      ) {
        setFields({
          ...fields,
          ...OhmsLaw.calcWattsResistance(parseFloat(current), parseFloat(voltage)),
        });
        setLastCalculated(['watts', 'resistance']);
      } else if (
        (wasLastCalculated('watts') && wasLastCalculated('current')) ||
        (resistance && voltage && current === '' && watts === '')
      ) {
        setFields({
          ...fields,
          ...OhmsLaw.calcWattsCurrent(parseFloat(resistance), parseFloat(voltage)),
        });
        setLastCalculated(['watts', 'current']);
      } else if (
        (wasLastCalculated('watts') && wasLastCalculated('voltage')) ||
        (resistance && current && voltage === '' && watts === '')
      ) {
        setFields({
          ...fields,
          ...OhmsLaw.calcWattsVoltage(parseFloat(resistance), parseFloat(current)),
        });
        setLastCalculated(['watts', 'voltage']);
      } else if (
        (wasLastCalculated('voltage') && wasLastCalculated('current')) ||
        (resistance && watts && voltage === '' && current === '')
      ) {
        setFields({
          ...fields,
          ...OhmsLaw.calcVoltageCurrent(parseFloat(resistance), parseFloat(watts)),
        });
        setLastCalculated(['voltage', 'current']);
      } else if (
        (wasLastCalculated('voltage') && wasLastCalculated('resistance')) ||
        (current && watts && voltage === '' && resistance === '')
      ) {
        setFields({
          ...fields,
          ...OhmsLaw.calcVoltageResistance(parseFloat(current), parseFloat(watts)),
        });
        setLastCalculated(['voltage', 'resistance']);
      } else if (
        (wasLastCalculated('current') && wasLastCalculated('resistance')) ||
        (voltage && watts && current === '' && resistance === '')
      ) {
        setFields({
          ...fields,
          ...OhmsLaw.calcCurrentResistance(parseFloat(watts), parseFloat(voltage)),
        });
        setLastCalculated(['current', 'resistance']);
      }
    },
    [fields, lastCalculated, wasLastCalculated],
  );

  return (
    <form onSubmit={calculate}>
      <Card>
        <CardContent>
          <div>
            <p>Supply at least two of the values to calculate the others.</p>
            <TextField
              label="I (Current)"
              InputLabelProps={{
                shrink: true,
              }}
              disabled={wasLastCalculated('current')}
              fullWidth
              name="current"
              margin="normal"
              onChange={onChange}
              value={fields.current}
            />
            <TextField
              label="V (Volts)"
              InputLabelProps={{
                shrink: true,
              }}
              disabled={wasLastCalculated('voltage')}
              fullWidth
              name="voltage"
              margin="normal"
              onChange={onChange}
              value={fields.voltage}
            />
            <TextField
              label="R (Resistance)"
              InputLabelProps={{
                shrink: true,
              }}
              disabled={wasLastCalculated('resistance')}
              fullWidth
              name="resistance"
              margin="normal"
              onChange={onChange}
              value={fields.resistance}
            />
            <TextField
              label="P (Watts)"
              InputLabelProps={{
                shrink: true,
              }}
              disabled={wasLastCalculated('watts')}
              fullWidth
              name="watts"
              margin="normal"
              onChange={onChange}
              value={fields.watts}
            />
          </div>
        </CardContent>
        <CardActions>
          <Button type="button" tabIndex="6" onClick={clear}>
            Clear
          </Button>
          <Button type="submit" color="primary" variant="contained" style={{ marginLeft: 'auto' }}>
            Calculate
          </Button>
        </CardActions>
        <CardContent>
          <div style={{ textAlign: 'center' }}>
            <img
              src={FormulaWheelElectronics}
              className="Omh's Law Formula Wheel"
              alt="Omh's Law formula Wheel"
            />
          </div>
        </CardContent>
      </Card>
    </form>
  );
}
